import { BusyButton } from '@ztechapps/webui.button'
import type { BlogTranscribingMetadata } from '@ztechapps/common.contract'
import { BlogTranscribingStatusEnum } from '@ztechapps/common.contract'
import { getStatusLabel, isStatusInProgress, isStatusCancellable } from '@ztechapps/blog.common'
import { useError, useToast, useConfirmDialog } from '@ztechapps/common.hook'
import { useTranscribeBlogVideos } from '../hooks/useTranscribeBlogVideos'
import { useCancelBlogTranscriptionJob } from '../hooks/useCancelBlogTranscriptionJob'

export type TranscribeVideosButtonProps = {
  /** Blog ID to transcribe videos for */
  blogId: string
  /** Whether the blog content has video URLs */
  hasVideos?: boolean
  /** Current transcription metadata from the blog entity (for persisted state) */
  transcribingMetadata?: BlogTranscribingMetadata
  /** Whether the button is disabled */
  disabled?: boolean
  /** Reason the button is disabled, rendered as its tooltip when supplied */
  disabledReason?: string
}

/**
 * Button component for initiating video transcription on a blog post.
 *
 * @description
 * - Displays a button to start video transcription for a blog post
 * - Shows status updates from transcribingMetadata (updated via parent component)
 * - Allows cancellation of pending/delayed jobs
 * - Only visible when hasVideos prop is true
 * - State survives page refresh via transcribingMetadata from the entity
 *
 * @example
 * ```tsx
 * <TranscribeVideosButton
 *   blogId="blog-123"
 *   hasVideos={true}
 *   transcribingMetadata={blog.transcribingMetadata}
 * />
 * ```
 */
export const TranscribeVideosButton = ({
  blogId,
  hasVideos = false,
  transcribingMetadata,
  disabled = false,
  disabledReason,
}: TranscribeVideosButtonProps) => {
  const [transcribe, { loading: transcribeLoading }] = useTranscribeBlogVideos()
  const [cancelJob, { loading: cancelLoading }] = useCancelBlogTranscriptionJob()

  const { showSuccess, showError, showInfo } = useToast()
  const { onError } = useError()

  const handleTranscribe = async () => {
    try {
      const blog = await transcribe({ blogId })

      if (!blog?.transcribingMetadata) {
        showError({ message: 'Failed to start transcription job' })
        return
      }

      const { videoCount } = blog.transcribingMetadata
      showSuccess({ message: `Transcription started for ${videoCount} video(s)` })
    } catch (err) {
      // Handle BlogNoVideosFoundException
      if (err instanceof Error && err.message.includes('No videos found')) {
        showInfo({ message: 'No videos found in the blog content' })
      } else {
        onError(err instanceof Error ? err : new Error(String(err)))
      }
    }
  }

  const { onConfirm: handleCancelConfirm } = useConfirmDialog({
    title: 'Cancel Transcription',
    text: 'Are you sure you want to cancel the transcription job?',
    buttons: {
      confirm: {
        text: 'Yes, cancel it',
      },
    },
    confirmed: async () => {
      try {
        const blog = await cancelJob({ blogId })

        if (blog?.transcribingMetadata?.status === BlogTranscribingStatusEnum.cancelled) {
          showSuccess({ message: 'Transcription job cancelled' })
        } else {
          showError({ message: 'Failed to cancel transcription job' })
        }
      } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)))
      }
    },
  })

  // Don't render if no videos in content
  if (!hasVideos) {
    return null
  }

  // Determine current status and processing state from transcribingMetadata
  const currentStatus = transcribingMetadata?.status
  const isInProgress = isStatusInProgress(currentStatus)
  const showProcessingUI = isInProgress

  // Show processing state with cancel option
  if (showProcessingUI && currentStatus) {
    const statusLabel = getStatusLabel(currentStatus as BlogTranscribingStatusEnum)

    // Active jobs cannot be cancelled - show status only
    if (currentStatus === BlogTranscribingStatusEnum.active) {
      return (
        <BusyButton
          icon="video"
          text={statusLabel}
          variant="info"
          disabled={true}
          title="Job is actively processing and cannot be cancelled"
        />
      )
    }

    // Waiting/delayed jobs can be cancelled
    if (isStatusCancellable(currentStatus)) {
      return (
        <BusyButton
          icon="times"
          text={`${statusLabel} -- Cancel`}
          variant="danger"
          onClick={handleCancelConfirm}
          isBusy={cancelLoading}
          busyText="Cancelling..."
          title="Cancel transcription job"
        />
      )
    }

    // Failed/completed states - show status only
    return (
      <BusyButton
        icon="info"
        text={statusLabel}
        variant="secondary"
        disabled={true}
        title={`Transcription ${statusLabel.toLowerCase()}`}
      />
    )
  }

  // Show start transcription button - disabled when job is in progress
  return (
    <BusyButton
      icon="video"
      text="Transcribe Videos"
      onClick={handleTranscribe}
      isBusy={transcribeLoading}
      busyText="Starting..."
      disabled={disabled || isInProgress}
      title={disabledReason || 'Extract and transcribe videos from this blog post'}
    />
  )
}
